import { arrayOf, func, number, shape, string } from 'prop-types';
import React, { useState } from 'react';
import classnames from 'classnames';
import Image from 'components/Image';
import LazyLoad from 'components/LazyLoad';
import Loading from 'components/Loading';
import CloseIcon from 'components/Icons/Close';
import ArrowIcon from 'components/Icons/Arrow';
import { MQ_LARGE, MQ_MEDIUM } from 'constants/mq';
import styles from './styles';

function Lightbox({ images, currentIndex, toggleFullScreen, navFunctions }) {
  const [index, setIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);

  const handlePrevArrow = e => {
    e.preventDefault();
    setIsLoading(true);
    setIndex(index > 0 ? index - 1 : images.length - 1);
    // Call parent fn to update the UI
    if (navFunctions.handlePrevArrow) {
      navFunctions.handlePrevArrow(e);
    }
  };

  const handleNextArrow = e => {
    e.preventDefault();
    setIsLoading(true);
    setIndex(index < images.length - 1 ? index + 1 : 0);
    // Call parent fn to update the UI
    if (navFunctions.handleNextArrow) {
      navFunctions.handleNextArrow(e);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'ArrowLeft') {
      handlePrevArrow(e);
    } else if (e.key === 'ArrowRight') {
      handleNextArrow(e);
    } else if (e.key === 'Escape') {
      toggleFullScreen(e);
    }
  };

  const handleImageOnLoad = () => {
    setIsLoading(false);
  };

  return (
    <div
      className="Lightbox"
      onKeyDown={e => handleKeyDown(e)}
      role="button"
      tabIndex="0"
    >
      <LazyLoad once>
        <Image
          className={classnames({
            'Lightbox__image--landscape':
              images[index].originalWidth > images[index].originalHeight,
            'Lightbox__image--portrait':
              images[index].originalHeight > images[index].originalWidth,
            'Lightbox__image--loading': isLoading
          })}
          url={images[index].url}
          alt={images[index].alt}
          maxSize={{
            width: 800,
            aspectRatio:
              images[index].originalWidth && images[index].originalHeight
                ? images[index].originalWidth / images[index].originalHeight
                : undefined
          }}
          sourceSizes={[
            {
              media: MQ_LARGE,
              layoutWidth: { units: 'px', value: 845 }
            },
            {
              media: MQ_MEDIUM,
              layoutWidth: { units: 'px', value: 696 }
            },
            { layoutWidth: { units: 'px', value: 382 } }
          ]}
          onLoad={handleImageOnLoad}
        />
        <div className="Lightbox__caption">
          {isLoading
            ? 'Loading...'
            : `${images[index].caption} (Image credit: ${images[index].credit})`}
        </div>
      </LazyLoad>
      {isLoading && <Loading className="Lightbox__loading" height="100vh" />}
      <button
        className="Lightbox__icon Lightbox__prev"
        onClick={e => handlePrevArrow(e)}
        aria-label="Previous image"
      >
        <ArrowIcon rotate={180} />
      </button>
      <button
        className="Lightbox__icon Lightbox__next"
        onClick={e => handleNextArrow(e)}
        aria-label="Next image"
      >
        <ArrowIcon />
      </button>
      <button
        className="Lightbox__icon Lightbox__close"
        onClick={e => toggleFullScreen(e)}
      >
        <CloseIcon />
      </button>
      <style jsx>{styles}</style>
    </div>
  );
}

Lightbox.propTypes = {
  images: arrayOf(
    shape({
      url: string,
      caption: string,
      credit: string,
      alt: string,
      originalWidth: number,
      originalHeight: number
    })
  ).isRequired,
  currentIndex: number,
  toggleFullScreen: func.isRequired,
  navFunctions: shape({
    handlePrevArrow: func,
    handleNextArrow: func
  })
};

Lightbox.defaultProps = {
  currentIndex: 0,
  navFunctions: undefined
};

export default Lightbox;
